/**
 * usePullToRefresh — minimal pull-to-refresh hook for the PWA.
 *
 * Only activates in standalone display mode (i.e. when the user
 * launched the app from their home-screen icon). In a normal browser
 * tab the OS already handles pull-to-refresh, so we leave that
 * untouched and just no-op the hook there.
 *
 * Pull threshold: 70px past the resting position. Below that, the
 * pull is treated as a scroll attempt and ignored.
 */

import { useEffect, useRef, useState } from "react";

export function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  // Chrome / Android: matchMedia. iOS Safari: window.navigator.standalone.
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

const TRIGGER_PX = 70;
const MAX_PULL_PX = 110;

interface UsePullToRefreshState {
  pullDistance: number;
  refreshing: boolean;
}

export function usePullToRefresh(onRefresh: () => Promise<void> | void): UsePullToRefreshState {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startYRef = useRef(0);
  const activeRef = useRef(false);

  useEffect(() => {
    if (!isStandalonePwa()) return;

    const onTouchStart = (e: TouchEvent) => {
      // Only engage when the page is scrolled to the very top — that's
      // how native pull-to-refresh behaves and it keeps mid-scroll
      // gestures from accidentally triggering a refresh.
      if (window.scrollY > 0 || refreshing) {
        activeRef.current = false;
        return;
      }
      activeRef.current = true;
      startYRef.current = e.touches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!activeRef.current || refreshing) return;
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy <= 0) {
        setPullDistance(0);
        return;
      }
      // Rubber-band: damp the pull past 50px so it feels physical.
      const damped = dy < 50 ? dy : 50 + (dy - 50) * 0.5;
      setPullDistance(Math.min(damped, MAX_PULL_PX));
    };

    const onTouchEnd = async () => {
      if (!activeRef.current) return;
      activeRef.current = false;
      if (pullDistance >= TRIGGER_PX && !refreshing) {
        setRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setRefreshing(false);
          setPullDistance(0);
        }
      } else {
        setPullDistance(0);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [onRefresh, pullDistance, refreshing]);

  return { pullDistance, refreshing };
}
